import { motion, AnimatePresence } from 'framer-motion';
import { useI18n } from '@/lib/i18n';
import { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const fallbackServices = [
  {
    titleRu: 'Креативный дирекшн', titleEn: 'Creative Direction',
    descRu: 'Я помогаю брендам и проектам формировать визуальные системы с ясной позицией и смыслом.\nРаботаю на уровне идеи, стратегии и художественного решения — от вопроса до финального визуального языка.',
    descEn: 'I help brands and projects build visual systems with a clear stance and meaning.\nWorking at the level of idea, strategy, and artistic decision — from the question to the final visual language.',
  },
  {
    titleRu: 'Арт-дирекшн', titleEn: 'Art Direction',
    descRu: 'Арт-дирекшн для рекламных, культурных и шоу-проектов.\nЯ отвечаю не только за эстетику, но и за логику визуальных решений: почему именно так и что это говорит зрителю.',
    descEn: 'Art direction for advertising, cultural, and show projects.\nI am responsible not only for aesthetics, but also for the logic of visual decisions: why exactly this way and what it says to the viewer.',
  },
  {
    titleRu: 'Визуальные концепции', titleEn: 'Visual Concepts',
    descRu: 'Я разрабатываю отдельные элементы и полноценные кейвижуалы, в которых форма подчинена смыслу.',
    descEn: 'I develop individual elements and full key visuals where form serves meaning.',
  },
];

const Services = () => {
  const { lang, t } = useI18n();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const { data: settings } = useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const { data } = await supabase.from('site_settings').select('*').maybeSingle();
      return data;
    },
  });

  const { data: dbServices } = useQuery({
    queryKey: ['services'],
    queryFn: async () => {
      const { data } = await supabase.from('services').select('*').order('sort_order');
      return data || [];
    },
  });

  const sectionTitle = lang === 'ru'
    ? (settings?.services_title_ru) || 'Услуги'
    : (settings?.services_title_en) || 'Services';

  const services = dbServices && dbServices.length > 0
    ? dbServices.map(s => ({
        titleRu: s.title_ru, titleEn: s.title_en,
        descRu: s.description_ru, descEn: s.description_en,
      }))
    : fallbackServices;

  const toggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  return (
    <section id="services" className="py-24 md:py-32">
      <div className="container">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl md:text-6xl lg:text-7xl font-display font-bold mb-16 text-center uppercase"
        >
          {sectionTitle}
        </motion.h2>

        <div className="max-w-3xl mx-auto">
          {services.map((service, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="border-b border-border"
            >
              <button
                onClick={() => toggle(i)}
                className="w-full flex items-center justify-between py-6 md:py-8 text-left group"
              >
                <span className="text-lg md:text-2xl font-display font-bold uppercase tracking-wide group-hover:text-primary transition-colors duration-300">
                  {lang === 'ru' ? service.titleRu : service.titleEn}
                </span>
                <span className="text-primary ml-4 flex-shrink-0">
                  {openIndex === i ? <Minus size={20} /> : <Plus size={20} />}
                </span>
              </button>
              <AnimatePresence>
                {openIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    className="overflow-hidden"
                  >
                    <div className="pb-6 md:pb-8 px-10 md:px-16 text-muted-foreground text-sm md:text-base leading-relaxed space-y-2">
                      {(lang === 'ru' ? service.descRu : service.descEn).split('\n').map((p, j) => (
                        <p key={j}>{p}</p>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
